<template>
    <NuxtLink
        :to="tileLink?.url"
        :target="tileLink?.target"
        class="group rounded-card-md relative block h-full w-full overflow-hidden"
        :class="{
            'surface-primary': !isEmpty(tile.ColorPalette),
            // Mobile only:
            // If there is TextOnHover, the whole tile should not look clickable.
            'max-lg:cursor-default': tile.TextOnHover,

            // If there is no TextOnHover, the whole tile remains clickable.
            'max-lg:cursor-pointer': !tile.TextOnHover && tileLink?.url,

            // Desktop keeps normal linked-tile behaviour.
            'lg:cursor-pointer': tileLink?.url,
        }"
        @click="handleTileClick"
    >
        <Image
            class="absolute! inset-0 z-0"
            :image="tile.Image"
        />

        <div
            class="text-neutral-base absolute inset-0 z-10 transition-colors duration-300"
            data-mode="brand-invert"
        >
            <!-- overlay -->
            <div
                class="pointer-events-none absolute inset-0 z-0 bg-black transition-all duration-500 lg:group-hover:opacity-100 lg:group-hover:duration-500"
                :class="{
                    'lg:opacity-30': ['30', null].includes(tile.OverlayOpacity),
                    'lg:opacity-50': tile.OverlayOpacity === '50',
                    'lg:opacity-70': tile.OverlayOpacity === '70',
                    'lg:opacity-0': tile.OverlayOpacity === '0',
                    'max-lg:opacity-30': ['30', null].includes(tile.OverlayOpacity) && !mobileIsActive,
                    'max-lg:opacity-50': tile.OverlayOpacity === '50' && !mobileIsActive,
                    'max-lg:opacity-70': tile.OverlayOpacity === '70' && !mobileIsActive,
                    'max-lg:opacity-0': tile.OverlayOpacity === '0' && !mobileIsActive,
                    'lg:bg-brand-opacity-80 lg:group-hover:bg-brand-opacity-80': ['80', null].includes(
                        tile.OverlayOpacityHover,
                    ),
                    'lg:bg-brand-opacity-60 lg:group-hover:bg-brand-opacity-60': tile.OverlayOpacityHover === '60',
                    'lg:bg-brand-opacity-40 lg:group-hover:bg-brand-opacity-40': tile.OverlayOpacityHover === '40',
                    'max-lg:opacity-100': mobileIsActive,
                    'max-lg:bg-brand-opacity-80': ['80', null].includes(tile.OverlayOpacityHover) && mobileIsActive,
                    'max-lg:bg-brand-opacity-60': tile.OverlayOpacityHover === '60' && mobileIsActive,
                    'max-lg:bg-brand-opacity-40': tile.OverlayOpacityHover === '40' && mobileIsActive,
                }"
            />

            <div
                class="absolute inset-6 z-10 flex flex-col overflow-hidden"
                :class="{
                    'items-start': ['top-left', 'left', 'bottom-left'].includes(tile.ContentPosition),
                    'items-center': ['top', 'center', 'bottom'].includes(tile.ContentPosition),
                    'items-end': ['top-right', 'right', 'bottom-right'].includes(tile.ContentPosition),
                    'justify-start': ['top-left', 'top', 'top-right'].includes(tile.ContentPosition),
                    'justify-center': ['left', 'center', 'right'].includes(tile.ContentPosition),
                    'justify-end': ['bottom-left', 'bottom', 'bottom-right'].includes(tile.ContentPosition),
                    'max-lg:pb-12': tile.TextOnHover,
                }"
            >
                <div
                    ref="textContainer"
                    class="relative grid w-full grid-rows-[auto_0fr] space-y-1 transition-[grid-template-rows] duration-300 ease-in-out lg:group-hover:grid-rows-[auto_1fr]"
                    :class="{
                        'text-center': tile.TextPosition === 'centred',
                        'max-lg:grid-rows-[auto_1fr]': mobileIsActive || (!tile.TextOnHover && tileLink?.url),
                    }"
                >
                    <div>
                        <div
                            v-if="tile.Icon?.name"
                            :class="{
                                'flex justify-center': tile.TextPosition === 'centred',
                            }"
                        >
                            <DIcon
                                :size="iconSize"
                                :icon="iconName(tile.Icon)"
                                class="shape-brand-accent"
                            />
                        </div>

                        <BlocksTileLayoutBlockCounterAnimation
                            v-if="tile.NumberCountAnimation && tile.NumberCountValue"
                            :prefix="tile.NumberCountPrefix"
                            :value="tile.NumberCountValue"
                            :suffix="tile.NumberCountSuffix"
                            :color="tile.NumberCountColour"
                            :alignment="tile.TextPosition"
                        />

                        <p
                            v-if="tile.Subheading"
                            class="leading-sm font-ux text-sm font-(--font-weight-subheading) tracking-wider uppercase"
                            :class="{
                                'text-neutral-strong': tile.SubHeadingColour !== 'accent',
                                'text-subheading': tile.SubHeadingColour === 'accent',
                            }"
                        >
                            {{ tile.Subheading }}
                        </p>
                        <p
                            v-if="tile.Heading"
                            class="font-brand-heading font-heading text-heading"
                            :class="{
                                'leading-xl text-xl': ['sm', null].includes(tile.TextSize),
                                'leading-4xl text-4xl': tile.TextSize === 'md',
                                'leading-5xl text-5xl': tile.TextSize === 'lg',
                            }"
                        >
                            {{ tile.Heading }}
                        </p>
                    </div>
                    <div
                        ref="bodyText"
                        class="collapse relative overflow-hidden opacity-0 transition-opacity lg:group-hover:visible lg:group-hover:opacity-100 lg:group-hover:delay-300 lg:group-hover:duration-500"
                        :class="{
                            'lg:hidden': !tile.TextOnHover && !tileLink?.text,
                            'max-lg:hidden': !tile.TextOnHover && tileLink?.url && !tileLink?.text,
                            'delay-300 duration-500 max-lg:visible max-lg:opacity-100':
                                mobileIsActive || (!tile.TextOnHover && tileLink?.url),
                        }"
                    >
                        <div class="h-full">
                            <p
                                v-if="tile.TextOnHover"
                                class="font-ux leading-base text-base font-normal whitespace-pre-wrap text-(--text-body)"
                            >
                                {{ tile.TextOnHover }}
                            </p>
                            <div
                                v-if="tileLink?.url"
                                class="inline-flex"
                                data-tile-link-trigger="true"
                            >
                                <DButton
                                    v-if="tileLink?.text"
                                    class="mt-2"
                                    :theme="buttonTheme(tileLink.buttonStyle)"
                                >
                                    {{ tileLink.text }}
                                </DButton>
                                <DButton
                                    v-else
                                    class="mt-2 lg:hidden"
                                    :class="{
                                        hidden: !tile.TextOnHover,
                                    }"
                                    icon="arrow-right"
                                    theme="transparent"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Mobile expand/collapse toggle -->
            <div
                v-if="tile.TextOnHover"
                class="absolute bottom-6 z-20 rounded-full p-1 transition-transform duration-400 lg:hidden"
                :class="{
                    'left-1/2 -translate-x-1/2': isCenterAligned,
                    'right-6': !isCenterAligned,
                    'rotate-180': mobileIsActive,
                }"
                aria-label="Toggle tile details"
                @click.prevent.stop="toggleExpanded"
            >
                <DButton
                    icon="chevron-down"
                    size="sm"
                />
            </div>
        </div>
    </NuxtLink>
</template>

<script setup>
    import { DButton, DIcon } from '@digistorm/spark'

    const { iconName } = useIcons()

    const props = defineProps({
        tile: Object,
        animationOffset: Number,
        isActive: Boolean,
    })

    const { buttonTheme } = useButtonTheme()

    const isExpanded = ref(false)
    const hasManualMobileToggle = ref(false)
    const isMobileViewport = ref(false)
    const mobileIsActive = computed(() => {
        if (hasManualMobileToggle.value) {
            return isExpanded.value
        }

        return isExpanded.value || props.isActive
    })
    const hasMobileExpandableContent = computed(() => Boolean(props.tile?.TextOnHover))

    const isCenterAligned = computed(() => props.tile?.TextPosition === 'centred')

    const iconSize = computed(() => {
        switch (props.tile?.IconSize) {
            case 'sm':
                return 'w-6 h-6'
            case 'md':
            default:
                return 'w-8 h-8'
            case 'lg':
                return 'w-12 h-12'
        }
    })

    const tileLink = computed(() => {
        return first(props.tile?.Link)
    })

    const updateIsMobileViewport = () => {
        if (!import.meta.client) {
            return
        }

        isMobileViewport.value = window.matchMedia('(max-width: 1023px)').matches
    }

    const toggleExpanded = () => {
        const currentlyActive = mobileIsActive.value
        hasManualMobileToggle.value = true
        isExpanded.value = !currentlyActive
    }

    const handleTileClick = (event) => {
        // Desktop keeps the normal behaviour.
        // Mobile tiles without TextOnHover also keep whole-tile click behaviour. TextOnHover on mobile only allows the button/link area to be clickable, so block clicks elsewhere on the tile.
        if (!isMobileViewport.value || !hasMobileExpandableContent.value) {
            return
        }

        // On mobile with TextOnHover, allow only the tile DButton/link area to click through.
        if (event.target instanceof Element && event.target.closest('[data-tile-link-trigger="true"]')) {
            return
        }

        // Block the rest of the tile from navigating or toggling.
        event.preventDefault()
        event.stopPropagation()
    }

    watch(
        () => props.isActive,
        (isActive) => {
            if (isActive) {
                return
            }

            hasManualMobileToggle.value = false
            isExpanded.value = false
        },
    )

    onMounted(() => {
        updateIsMobileViewport()
        window.addEventListener('resize', updateIsMobileViewport)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('resize', updateIsMobileViewport)
    })
</script>
